import { useState } from "react";
import { motion } from "framer-motion";
import { STORE_PHONE } from "@/lib/brand";
import { MapPin, Phone, Clock, Navigation, Car, ExternalLink, Sparkles, Loader2 } from "lucide-react";

const STORE_QUERY = "W359N5002 Brown Street Suite 211 Oconomowoc WI 53066";
const STORE_MAP_LINK = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(STORE_QUERY)}`;

/**
 * Editorial "Visit the Boutique" section.
 *
 * Left column: store card with address, phone, hours, in-page directions input.
 * Right column: Google Maps `output=embed` iframe.
 *
 * The directions input updates the iframe URL from `?q=<store>` (map view) to
 * `?saddr=<origin>&daddr=<store>` (directions), so the user gets turn-by-turn
 * inline without ever leaving the page. Falls back to "Open in Google Maps"
 * for full navigation.
 */
export default function VisitBoutique() {
  const [origin, setOrigin] = useState("");
  const [loading, setLoading] = useState(false);
  const [dirActive, setDirActive] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);

  const baseUrl = `https://maps.google.com/maps?q=${encodeURIComponent(STORE_QUERY)}&t=&z=13&ie=UTF8&iwloc=&output=embed`;
  const directionsUrl = dirActive && origin.trim()
    ? `https://maps.google.com/maps?saddr=${encodeURIComponent(origin.trim())}&daddr=${encodeURIComponent(STORE_QUERY)}&t=&ie=UTF8&iwloc=&output=embed`
    : baseUrl;

  const submit = (e) => {
    e.preventDefault();
    if (!origin.trim()) return;
    setLoading(true);
    setDirActive(true);
    setIframeKey((k) => k + 1);
    // Give iframe a beat to update, then release loading state
    setTimeout(() => setLoading(false), 800);
    try {
      window.posthog?.capture("map_directions_submit", { has_origin: true });
    } catch {
      /* noop */
    }
  };

  const reset = () => {
    setOrigin("");
    setDirActive(false);
    setIframeKey((k) => k + 1);
  };

  return (
    <section
      data-testid="visit-boutique"
      className="relative mt-32 py-24 md:py-28 overflow-hidden bg-ink text-cream"
    >
      {/* Ambient background glows */}
      <div className="absolute -top-40 -left-32 w-[600px] h-[600px] rounded-full bg-rosemetal/15 blur-3xl" aria-hidden />
      <div className="absolute -bottom-40 -right-40 w-[700px] h-[700px] rounded-full bg-champagne/12 blur-3xl" aria-hidden />
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-champagne to-transparent" aria-hidden />

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-[440px_1fr] gap-10 lg:gap-14 items-stretch">
          {/* Left: store card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col"
          >
            <p className="font-label text-[10px] text-champagne tracking-[0.32em] mb-4 inline-flex items-center gap-2">
              <span className="w-6 h-px bg-champagne/60" /> COME SEE US IN PERSON
            </p>
            <h2 className="font-display text-5xl md:text-6xl lg:text-[64px] leading-[0.95]">
              The boutique.<br />
              <span className="italic text-champagne">In real light.</span>
            </h2>
            <p className="font-body text-base text-cream/65 mt-6 leading-relaxed max-w-md">
              Every gown looks different in Heather's fitting rooms. Come try on, meet the
              seamstress, and get pinned &amp; tailored to you — the way formalwear was always meant to be.
            </p>

            {/* NAP block */}
            <div className="mt-10 space-y-5 border-l border-cream/15 pl-5">
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 shrink-0 rounded-full border border-cream/25 flex items-center justify-center">
                  <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
                </span>
                <div>
                  <p className="font-label text-[9px] text-cream/45 tracking-widest">ADDRESS</p>
                  <p className="font-display text-lg leading-tight mt-1">W359N5002 Brown Street, Suite 211</p>
                  <p className="font-body text-xs text-cream/50 mt-0.5">Oconomowoc, WI 53066</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 shrink-0 rounded-full border border-cream/25 flex items-center justify-center">
                  <Phone className="w-3.5 h-3.5" strokeWidth={1.5} />
                </span>
                <div>
                  <p className="font-label text-[9px] text-cream/45 tracking-widest">CALL OR TEXT</p>
                  <a href={`tel:${STORE_PHONE.replace(/\D/g, "")}`} data-testid="visit-phone" className="font-display text-lg mt-1 block hover:text-champagne transition-colors">{STORE_PHONE}</a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 shrink-0 rounded-full border border-cream/25 flex items-center justify-center">
                  <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
                </span>
                <div>
                  <p className="font-label text-[9px] text-cream/45 tracking-widest">HOURS</p>
                  <p className="font-body text-sm mt-1 leading-relaxed">Mon–Sat · 11:00 AM – 4:30 PM</p>
                  <p className="font-body text-xs text-cream/45">Sunday · Closed</p>
                </div>
              </div>
            </div>

            {/* Directions form */}
            <form
              onSubmit={submit}
              data-testid="visit-directions-form"
              className="mt-10 pt-8 border-t border-cream/15"
            >
              <p className="font-label text-[10px] text-champagne tracking-[0.28em] mb-3 inline-flex items-center gap-2">
                <Navigation className="w-3 h-3" strokeWidth={2} /> DIRECTIONS FROM YOUR PLACE
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={origin}
                  onChange={(e) => setOrigin(e.target.value)}
                  placeholder="Your address, city, or ZIP…"
                  data-testid="visit-origin-input"
                  className="flex-1 bg-cream/10 border border-cream/25 rounded-sm px-3.5 py-3 font-body text-sm text-cream placeholder:text-cream/50 outline-none focus:border-champagne focus:bg-cream/15 transition-colors"
                />
                <button
                  type="submit"
                  disabled={!origin.trim() || loading}
                  data-testid="visit-directions-submit"
                  className="bg-champagne text-ink px-5 py-3 font-label text-[10px] tracking-[0.28em] rounded-sm hover:bg-ivory transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 whitespace-nowrap"
                >
                  {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Car className="w-3.5 h-3.5" strokeWidth={2} />}
                  ROUTE
                </button>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <p className="font-label text-[9px] tracking-widest text-cream/40">DIRECTIONS APPEAR IN THE MAP →</p>
                {dirActive && (
                  <button
                    type="button"
                    onClick={reset}
                    data-testid="visit-directions-reset"
                    className="font-label text-[9px] tracking-widest text-champagne hover:text-cream"
                  >
                    RESET ↺
                  </button>
                )}
              </div>
            </form>

            {/* Secondary — open in maps */}
            <a
              href={STORE_MAP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="visit-open-google-maps"
              className="mt-6 inline-flex items-center gap-2 font-label text-[10px] tracking-[0.28em] text-cream/70 hover:text-champagne transition-colors self-start"
            >
              OPEN IN GOOGLE MAPS <ExternalLink className="w-3 h-3" />
            </a>
          </motion.div>

          {/* Right: map */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="relative rounded-sm overflow-hidden border border-cream/15 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.55)] min-h-[520px] lg:min-h-[600px]">
              <iframe
                key={iframeKey}
                title="Heather's Boutique map"
                data-testid="visit-map-iframe"
                src={directionsUrl}
                className="absolute inset-0 w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
                style={{ border: 0, filter: "saturate(0.95) contrast(1.02)" }}
              />
              {/* Corner overlay pin badge */}
              <div className="absolute top-4 left-4 pointer-events-none">
                <div className="bg-ivory/95 backdrop-blur-md rounded-sm shadow-lg px-3 py-2 border border-champagne/40 inline-flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-rosemetal flex items-center justify-center">
                    <MapPin className="w-3 h-3 text-cream" strokeWidth={2.5} />
                  </span>
                  <div>
                    <p className="font-label text-[8px] text-rosemetal tracking-widest leading-none">HEATHER'S BOUTIQUE</p>
                    <p className="font-display text-sm text-ink leading-tight mt-0.5">Oconomowoc, WI</p>
                  </div>
                </div>
              </div>
              {/* Ambient reflection frame */}
              <div className="pointer-events-none absolute inset-0 rounded-sm ring-1 ring-champagne/10" />
            </div>

            {/* Trust rail below map */}
            <div className="mt-6 grid grid-cols-3 gap-3">
              {[
                { k: "WI-1", v: "Wisconsin's oldest school-first boutique" },
                { k: "1,200+", v: "Students personally styled" },
                { k: "5.0 ★", v: "Google reviews" },
              ].map((s) => (
                <div key={s.k} className="border border-cream/12 rounded-sm p-4 bg-cream/[.03]">
                  <p className="font-display text-2xl text-champagne leading-none">{s.k}</p>
                  <p className="font-body text-[11px] text-cream/55 mt-2 leading-snug">{s.v}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom rail — CTA */}
        <div className="mt-14 pt-10 border-t border-cream/10 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-label text-[10px] text-champagne tracking-[0.28em] inline-flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> BOOK A PRIVATE FITTING
            </p>
            <p className="font-display text-2xl italic text-cream mt-2">Heather styles you like her own.</p>
          </div>
          <div className="flex gap-3">
            <a
              href={`tel:${STORE_PHONE.replace(/\D/g, "")}`}
              data-testid="visit-call-cta"
              className="bg-champagne text-ink px-6 py-3 font-label text-[10px] tracking-[0.28em] rounded-full hover:bg-ivory transition-colors inline-flex items-center gap-2"
            >
              <Phone className="w-3 h-3" strokeWidth={2.5} /> BOOK A FITTING
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
